import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { getLeaderboards, type LeaderboardEntry as LeaderboardRow } from "@/api/leaderboards";
import { formatDate, formatMetric } from "@/lib/format";
import { PageHeader } from "@/components/PageHeader";
import { Pagination } from "@/components/Pagination";

const LB_MISSION = "Racing - Basics";

const LB_PAGE_SIZE = 10;

function rankMedal(rank: number): string {
  if (rank === 1) return "gold";
  if (rank === 2) return "silver";
  if (rank === 3) return "bronze";
  return "";
}

function PodiumCard({ row, place }: { row: LeaderboardRow; place: 1 | 2 | 3 }) {
  const hasLap = row.best_lap_seconds !== null;
  const flight = row.best_lap_flight;
  return (
    <article
      className={`podium-card podium-card--${place} podium-card--${rankMedal(place)}`}
    >
      <div className="podium-card__rank">
        <span className="podium-card__medal" aria-hidden="true">
          {place === 1 ? "1" : place === 2 ? "2" : "3"}
        </span>
        <span className="podium-card__place-label">
          {place === 1 ? "Champion" : place === 2 ? "Runner-up" : "Third"}
        </span>
      </div>
      <div className="podium-card__pilot">
        <strong>{row.username}</strong>
        <small>
          {row.flight_count} flight{row.flight_count === 1 ? "" : "s"}
        </small>
      </div>
      <div className="podium-card__time">
        {hasLap ? (
          <>
            <span className="podium-card__time-value">
              {formatMetric(row.best_lap_seconds, 2, "")}
            </span>
            <span className="podium-card__time-unit">sec</span>
          </>
        ) : (
          <span className="podium-card__time-none">No lap</span>
        )}
      </div>
      {flight && (
        <div className="podium-card__flight">
          <span className="podium-card__flight-name">
            {flight.session_name || flight.title || `Flight ${flight.id}`}
          </span>
          {flight.start_time && (
            <small>{formatDate(flight.start_time)}</small>
          )}
        </div>
      )}
      {row.can_open_flight && flight && (
        <Link className="button ghost podium-card__open" to={`/flights/${flight.id}`}>
          Open replay
        </Link>
      )}
    </article>
  );
}

export function LeaderboardsPage() {
  const [page, setPage] = useState(1);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["leaderboards", LB_MISSION],
    queryFn: () => getLeaderboards(LB_MISSION),
  });

  if (isLoading) return <p className="empty-state">Loading…</p>;
  if (isError) {
    return (
      <div className="callout danger">
        <strong>Could not load leaderboards</strong>
        <p>{error instanceof Error ? error.message : "Unknown error"}</p>
      </div>
    );
  }
  if (!data) return null;

  const rows = data.rows ?? [];
  const topRows = rows.filter((r) => r.best_lap_seconds !== null).slice(0, 3);
  const restRows = rows.slice(topRows.length);
  const pageCount = Math.max(1, Math.ceil(restRows.length / LB_PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const pageStart = (safePage - 1) * LB_PAGE_SIZE;
  const pageRows = restRows.slice(pageStart, pageStart + LB_PAGE_SIZE);

  // Render the podium in 2 / 1 / 3 order so the champion card is visually
  // centered and taller.
  const podiumOrder: (LeaderboardRow | null)[] = [
    topRows[1] ?? null,
    topRows[0] ?? null,
    topRows[2] ?? null,
  ];
  const placeByIndex: Record<number, 1 | 2 | 3> = { 0: 2, 1: 1, 2: 3 };

  return (
    <>
      <PageHeader
        title="Leaderboards"
        lede={`Best lap times for ${LB_MISSION} across all pilots.`}
      />

      {rows.length ? (
        <>
          {topRows.length > 0 && (
            <section className="podium">
              {podiumOrder.map((row, idx) =>
                row ? (
                  <PodiumCard key={row.rank} row={row} place={placeByIndex[idx]} />
                ) : (
                  <div
                    key={`empty-${idx}`}
                    className={`podium-card podium-card--${placeByIndex[idx]} podium-card--empty`}
                  >
                    <span className="podium-card__place-label">
                      {placeByIndex[idx] === 1
                        ? "Champion"
                        : placeByIndex[idx] === 2
                          ? "Runner-up"
                          : "Third"}
                    </span>
                    <span className="podium-card__time-none">No lap yet</span>
                  </div>
                ),
              )}
            </section>
          )}

          {restRows.length > 0 && (
            <section className="lb-board">
              {pageRows.map((row) => {
                const hasLap = row.best_lap_seconds !== null;
                return (
                  <div key={row.rank} className="lb-row">
                    <div className="lb-rank">
                      <span className="lb-rank-num">#{row.rank}</span>
                    </div>
                    <div className="lb-pilot">
                      <strong>{row.username}</strong>
                      {row.flight_count > 0 && (
                        <small>
                          {row.flight_count} flight{row.flight_count !== 1 && "s"}
                        </small>
                      )}
                    </div>
                    <div className="lb-time">
                      {hasLap ? (
                        <span className="lb-time-value">
                          {formatMetric(row.best_lap_seconds, 2, " s")}
                        </span>
                      ) : (
                        <span className="lb-time-none">No lap</span>
                      )}
                    </div>
                    <div className="lb-flight">
                      {row.best_lap_flight && (
                        <>
                          <span className="lb-flight-name">
                            {row.best_lap_flight.session_name ||
                              row.best_lap_flight.title ||
                              `Flight ${row.best_lap_flight.id}`}
                          </span>
                          <small>
                            ID {row.best_lap_flight.id}
                            {row.best_lap_flight.start_time && (
                              <> · {formatDate(row.best_lap_flight.start_time)}</>
                            )}
                          </small>
                        </>
                      )}
                    </div>
                    <div className="lb-action">
                      {row.can_open_flight && row.best_lap_flight && (
                        <Link
                          className="button ghost"
                          to={`/flights/${row.best_lap_flight.id}`}
                        >
                          Open detail
                        </Link>
                      )}
                    </div>
                  </div>
                );
              })}
              <Pagination
                page={safePage}
                pageCount={pageCount}
                total={restRows.length}
                itemLabel="entries"
                onChange={setPage}
              />
            </section>
          )}
        </>
      ) : (
        <section className="panel">
          <p className="empty-state">
            No leaderboard rows are available for the selected mission yet.
          </p>
        </section>
      )}
    </>
  );
}
